/**
 * Top の練習開始に必要な読み込み（認証・学習者一覧・利用可否・進捗）全体の待機上限（指示書 A5）。
 *
 * - 処理ごとではなく、読み込みの開始から合計で数える（既定 15秒。実測を見て調整する初期値）。
 * - 期限を過ぎたら、待っているすべての段階を LoadDeadlineError で終わらせ、中止できる通信（signal）は中止する。
 * - 新しい読み込み（再試行・学習者の切り替え）を始めたら、前の読み込みは cancel() で中止する。
 *
 * 期限を過ぎた・中止した読み込みの遅い応答は、呼び出し側で「最新の読み込みか」を確かめて捨てる（latestOnly.js）。
 */

export const TOP_REQUIRED_LOAD_TIMEOUT_MS = 15000

export class LoadDeadlineError extends Error {
  constructor(message = 'required data load timed out') {
    super(message)
    this.name = 'LoadDeadlineError'
  }
}

export const isLoadDeadlineError = (e) => e?.name === 'LoadDeadlineError'

/**
 * @param {{ timeoutMs?: number, setTimer?: typeof setTimeout, clearTimer?: typeof clearTimeout }} [opts]
 */
export function createLoadDeadline({ timeoutMs = TOP_REQUIRED_LOAD_TIMEOUT_MS, setTimer = setTimeout, clearTimer = clearTimeout } = {}) {
  const controller = new AbortController()
  let expired = false
  let done = false
  const waiting = new Set()

  const expire = () => {
    if (done) return
    expired = true
    done = true
    // 先に待っている段階を終わらせてから、通信を中止する（中止によるエラーより、期限切れを優先して伝える）
    const error = new LoadDeadlineError()
    for (const reject of waiting) reject(error)
    waiting.clear()
    controller.abort()
  }

  const timer = setTimer(expire, timeoutMs)

  return {
    /** 中止できる通信（fetch・Supabase の abortSignal）に渡す */
    signal: controller.signal,
    get expired() { return expired },
    /** 段階の Promise を、全体の期限付きで待つ */
    race(promise) {
      if (expired) return Promise.reject(new LoadDeadlineError())
      return new Promise((resolve, reject) => {
        waiting.add(reject)
        Promise.resolve(promise).then(
          (v) => { waiting.delete(reject); resolve(v) },
          (e) => { waiting.delete(reject); reject(e) }
        )
      })
    },
    /** 読み込みが終わった（成功・失敗）。期限の計測をやめる */
    finish() {
      done = true
      clearTimer(timer)
      waiting.clear()
    },
    /**
     * 新しい読み込みに置き換わった。期限の計測をやめ、通信を中止する。
     * 待っている段階も終わらせる（前の読み込みが待ち続けない）。呼び出し側は、最新の読み込みでなければ何もしない
     */
    cancel() {
      if (done) return
      done = true
      clearTimer(timer)
      const error = new LoadDeadlineError('required data load cancelled')
      error.cancelled = true
      for (const reject of waiting) reject(error)
      waiting.clear()
      controller.abort()
    }
  }
}
